/**
 * Turning what the wire says into what a person reads.
 *
 * All of it is here rather than inline in components because every one of these
 * is a place the API's shape is awkward in a way a reader must not see: `flags`
 * is a space-delimited string, `date` is unix SECONDS, and `from_addr_name` is
 * an unparsed header value.
 */
import { parseAddressList, displayName } from '@hearth/core/addresses'

/** The flag set, from the one space-delimited string the wire carries. */
export function flagSet(flags: string): Set<string> {
  return new Set(flags.trim().split(/\s+/).filter(Boolean))
}

export const isUnread = (flags: string): boolean => !flagSet(flags).has('\\Seen')
export const isFlagged = (flags: string): boolean => flagSet(flags).has('\\Flagged')

/**
 * The best short name for a sender.
 *
 * `from_addr_name` is the raw header — `"Whitfield, Anna" <anna@example.com>`,
 * possibly RFC 2047 encoded — and core already knows how to read one. Falling
 * back to the address, and then to the raw string, means a malformed header
 * shows SOMETHING rather than an empty column.
 */
export function senderName(from: string | null): string {
  if (!from) return '(no sender)'
  // `ParsedAddress.name` IS STILL ENCODED — parse.ts:36-40 says so explicitly,
  // because RFC 2047 must be decoded AFTER splitting on commas or an encoded
  // word containing one splits the list. So it goes through `displayName`,
  // which is the decode-and-collapse the index itself stores. Skipping that
  // step renders `=?utf-8?B?R3LDvMOfZSBBbm5h?=` in the sender column.
  const [first] = parseAddressList(from)
  if (first?.name) return displayName(first.name)
  if (first?.address) return first.address
  return displayName(from) || from
}

/**
 * A recipient list as a person reads it: every address decoded, in order.
 *
 * `senderName` TAKES THE FIRST AND THIS TAKES THEM ALL, which is the only
 * difference — both go through core's parser and `displayName`, so what the
 * reading pane shows and what the list column shows cannot come to disagree
 * about what a name is.
 *
 * HERE RATHER THAN IN THE READING PANE, because a conversation has one of these
 * per message and the shell has one for the row: two files rendering a To: line
 * is two places to forget the RFC 2047 decode, which is the bug this exists to
 * have fixed once.
 */
export function recipientLine(raw: string): string {
  const people = parseAddressList(raw)
  if (people.length === 0) return raw
  // `ParsedAddress.name` IS STILL ENCODED — the parser says so, because RFC 2047
  // has to be decoded AFTER splitting on commas or an encoded word containing
  // one splits the list. `displayName` is that decode, and it is the same one
  // `senderName` uses above.
  return people.map((person) => (person.name ? displayName(person.name) : person.address)).join(', ')
}

const DAY = 86_400
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const timeOnly = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
const dayMonth = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
const withYear = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * A list date, at the precision the distance justifies.
 *
 * Today is a time, this year is a day and month, older carries the year — the
 * convention every mail client uses, and the reason is column width: the date
 * column is the one part of a row whose width must not depend on its content,
 * or the subject column reflows as you scroll.
 */
export function listDate(seconds: number, now: number = Date.now() / 1000): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  const date = new Date(seconds * 1000)
  const age = now - seconds
  if (age < DAY && new Date(now * 1000).getDate() === date.getDate()) return timeOnly.format(date)
  if (new Date(now * 1000).getFullYear() === date.getFullYear()) return dayMonth.format(date)
  return withYear.format(date)
}

/** The full date, for the reading pane, where there is room to be unambiguous. */
export function fullDate(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  return new Date(seconds * 1000).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export { relative }

/**
 * How a count is spoken, INCLUDING when it is a bound rather than a total.
 *
 * `exact: false` means the true number is GREATER than `value` — the full-text
 * path caps at 10,001, and a count cut short by the scan budget can be far
 * smaller. Rendering that as a total is a lie a person then acts on, which is
 * why this never returns a bare number for an inexact count.
 */
export function countLabel(count: { value: number; exact: boolean } | null): string {
  if (!count) return ''
  const n = count.value.toLocaleString()
  if (count.exact) return `${n} message${count.value === 1 ? '' : 's'}`
  return `more than ${n} messages`
}

/**
 * "1 image", "3 images", "2 new messages".
 *
 * SAID IN THREE PLACES NOW, so said once here: the image banner, the unread
 * divider in a conversation, and the conversation's own header. English
 * pluralisation is not interesting; two spellings of it that drift apart are.
 */
export const many = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`

/** Bytes, for an attachment chip. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
